// Utility functions for browser notifications and favicon updates

// Create a canvas-based favicon with unread count
const createFaviconWithCount = (count) => {
  const canvas = document.createElement('canvas');
  const size = 32; // Standard favicon size
  canvas.width = size;
  canvas.height = size;
  
  const ctx = canvas.getContext('2d');
  
  // Clear canvas
  ctx.clearRect(0, 0, size, size);
  
  // Background circle (blue)
  ctx.fillStyle = '#3b82f6';
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
  ctx.fill();
  
  // Chat icon (white) - using text instead of emoji for better compatibility
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 14px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('💬', size / 2, size / 2);
  
  if (count > 0) {
    // Red notification circle
    const badgeSize = 14;
    const badgeX = size - badgeSize + 2;
    const badgeY = 2;
    
    ctx.fillStyle = '#ef4444';
    ctx.beginPath();
    ctx.arc(badgeX, badgeY + badgeSize / 2, badgeSize / 2, 0, 2 * Math.PI);
    ctx.fill();
    
    // White count text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 9px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    const countText = count > 99 ? '99+' : count.toString();
    ctx.fillText(countText, badgeX, badgeY + badgeSize / 2);
  }
  
  return canvas.toDataURL('image/png');
};

// Update favicon with unread count
export const updateFavicon = (count) => {
  try {
    // Remove existing favicon links
    const existingFavicons = document.querySelectorAll("link[rel*='icon']");
    existingFavicons.forEach(link => link.remove());
    
    // Create new favicon
    const favicon = document.createElement('link');
    favicon.type = 'image/png';
    favicon.rel = 'icon';
    favicon.id = 'dynamic-favicon';
    
    if (count > 0) {
      const faviconDataURL = createFaviconWithCount(count);
      favicon.href = faviconDataURL;
      
      // Also try setting as data URL directly
      const faviconData = document.createElement('link');
      faviconData.rel = 'icon';
      faviconData.href = faviconDataURL;
      faviconData.type = 'image/png';
      document.head.appendChild(faviconData);
    } else {
      // Reset to original favicon
      favicon.href = '/nesim-logo.jpg?v=4';
    }
    
    // Add to head
    document.head.appendChild(favicon);
    
    // Force favicon refresh by adding timestamp
    setTimeout(() => {
      if (count > 0) {
        const newFavicon = createFaviconWithCount(count) + '?t=' + Date.now();
        favicon.href = newFavicon;
      }
    }, 100);
    
  } catch (error) {
    console.error('Error updating favicon:', error);
  }
};

// Update browser tab title with unread count
export const updateTabTitle = (count) => {
  const baseTitle = 'NESIM Intranet';
  
  if (count > 0) {
    document.title = `(${count}) ${baseTitle}`;
  } else {
    document.title = baseTitle;
  }
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }
  
  if (Notification.permission === 'granted') {
    return true;
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  
  return false;
};

// Check current notification permission status
export const getNotificationPermission = () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};

// Show browser notification
export const showNotification = (title, options = {}) => {
  try {
    // Check if Notification is supported and permission is granted
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      console.log('Notifications not supported or permission not granted');
      return null;
    }

    // Check if we're in a secure context (required for notifications)
    if (!window.isSecureContext) {
      console.log('Notifications require a secure context (HTTPS)');
      return null;
    }

    // Try to create notification with proper error handling
    const notification = new Notification(title, {
      icon: '/nesim-logo.jpg',
      badge: '/nesim-logo.jpg',
      ...options
    });
    
    // Auto-close after 5 seconds
    setTimeout(() => {
      try {
        notification.close();
      } catch (e) {
        console.log('Error closing notification:', e);
      }
    }, 5000);
    
    // Focus window when notification is clicked
    notification.onclick = () => {
      try {
        window.focus();
        notification.close();
      } catch (e) {
        console.log('Error handling notification click:', e);
      }
    };
    
    return notification;
  } catch (error) {
    console.error('Error creating notification:', error);
    // Fallback: just log the message instead of showing notification
    console.log(`Notification: ${title}`, options);
    return null;
  }
};

// Check if tab is currently visible
export const isTabVisible = () => {
  return !document.hidden;
};

// Play notification sound
export const playNotificationSound = () => {
  try {
    // Create a simple notification sound using Web Audio API
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (error) {
    console.log('Could not play notification sound:', error);
  }
};
